import { useEffect, useState } from 'react';
import { X, Save, Loader2, Link2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useVertical } from '../../../context/VerticalContext';
import { useFinancialContext } from '../../../context/FinancialContext';
import { supabase } from '../../../lib/supabase';
import { localDate } from '../../../lib/periods';
import { errorMessage, reportError } from '../../../lib/errors';

interface ManualExpenseModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

interface CategoryRow {
    id: string;
    name: string;
    hmrc_category: string;
    default_vat_rate: number;
    is_tax_deductible: boolean;
}

interface ActiveJobRow {
    id: string;
    title: string | null;
    customer_name: string | null;
    vehicle_reg: string | null;
    status: string | null;
}

const todayISO = () => localDate(new Date());

export function ManualExpenseModal({ isOpen, onClose, onSuccess }: ManualExpenseModalProps) {
    const { activeOrgId } = useVertical();
    const { is_vat_registered } = useFinancialContext();

    const [categories, setCategories] = useState<CategoryRow[]>([]);
    const [activeJobs, setActiveJobs] = useState<ActiveJobRow[]>([]);

    const [date, setDate] = useState(todayISO());
    const [vendor, setVendor] = useState('');
    const [description, setDescription] = useState('');
    const [grossAmount, setGrossAmount] = useState('');
    const [vatAmount, setVatAmount] = useState('');
    const [categoryName, setCategoryName] = useState('');
    const [paymentMethod, setPaymentMethod] = useState<'card' | 'bank' | 'cash' | 'other'>('card');
    const [jobId, setJobId] = useState('');
    const [saving, setSaving] = useState(false);
    const [vatAutoCalc, setVatAutoCalc] = useState(true);

    // Reset every time the modal opens — never carry stale state across sessions.
    useEffect(() => {
        if (!isOpen) return;
        setDate(todayISO());
        setVendor('');
        setDescription('');
        setGrossAmount('');
        setVatAmount('');
        setCategoryName('');
        setPaymentMethod('card');
        setJobId('');
        setVatAutoCalc(true);
    }, [isOpen]);

    // Reference data — HMRC categories + active jobs for the link dropdown.
    useEffect(() => {
        if (!isOpen || !activeOrgId) return;
        const fetchAll = async () => {
            const [catRes, jobRes] = await Promise.all([
                supabase
                    .from('expense_categories')
                    .select('id, name, hmrc_category, default_vat_rate, is_tax_deductible')
                    .order('name'),
                supabase
                    .from('jobs')
                    .select('id, title, status, customer:customers(full_name), vehicle:vehicles(vrm)')
                    .eq('org_id', activeOrgId)
                    .is('deleted_at', null)
                    .not('status', 'in', '(cancelled,draft)')
                    .order('created_at', { ascending: false })
                    .limit(100),
            ]);
            setCategories((catRes.data ?? []) as CategoryRow[]);
            if (jobRes.error) {
                console.error('ManualExpenseModal: failed to load jobs for link dropdown', jobRes.error);
            }
            setActiveJobs((jobRes.data ?? []).map((j: any) => ({
                id: j.id,
                title: j.title,
                status: j.status,
                customer_name: j.customer?.full_name?.trim() || null,
                vehicle_reg: j.vehicle?.vrm ?? null,
            })));
        };
        fetchAll();
    }, [isOpen, activeOrgId]);

    // VAT auto-calc: 20% of net (= gross/6) when org is VAT-registered and the
    // chosen category is VAT-claimable. User can override by editing the VAT field.
    useEffect(() => {
        if (!vatAutoCalc) return;
        const gross = Number(grossAmount) || 0;
        if (!is_vat_registered || gross <= 0) {
            setVatAmount('');
            return;
        }
        const cat = categories.find((c) => c.name === categoryName);
        const rate = (cat?.default_vat_rate ?? 20) / 100;
        // gross includes VAT → net = gross / (1 + rate); vat = gross − net
        const vat = gross - gross / (1 + rate);
        setVatAmount(vat > 0 ? vat.toFixed(2) : '');
    }, [grossAmount, categoryName, categories, is_vat_registered, vatAutoCalc]);

    const handleSave = async () => {
        if (!activeOrgId) return;
        const gross = Number(grossAmount);
        if (!vendor.trim()) { toast.error('Enter a vendor name'); return; }
        if (!categoryName) { toast.error('Pick a category'); return; }
        if (!Number.isFinite(gross) || gross <= 0) { toast.error('Enter a positive amount'); return; }

        const vat = Number(vatAmount) || 0;
        if (vat < 0 || vat > gross) { toast.error('VAT must be between 0 and the gross total'); return; }

        setSaving(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            const userId = session?.user?.id;
            if (!userId) throw new Error('Not signed in');

            // 1. Header (the expense document)
            const { data: header, error: hErr } = await supabase
                .from('ledger_headers')
                .insert({
                    org_id: activeOrgId,
                    user_id: userId,
                    transaction_type: 'expense',
                    status: 'posted',
                    date,
                    vendor: vendor.trim(),
                    party_name: vendor.trim(),
                    description: description.trim() || vendor.trim(),
                    total_gross: gross,
                    vat_amount: vat,
                    total_amount: gross,
                    total_vat: vat,
                    payment_method: paymentMethod,
                    job_id: jobId || null,
                    created_by: 'manual',
                })
                .select('id')
                .single();
            if (hErr) throw hErr;

            // 2. Single line item carrying the category — what fn_get_profit_and_loss
            //    joins on (li.category = expense_categories.name).
            const net = gross - vat;
            const { error: liErr } = await supabase
                .from('ledger_line_items')
                .insert({
                    org_id: activeOrgId,
                    header_id: header.id,
                    header_date: date,
                    category: categoryName,
                    description: description.trim() || vendor.trim(),
                    quantity: 1,
                    unit_price: net,
                    total: net,
                });
            if (liErr) throw liErr;

            // 3. Does this agree with what the job says the parts cost?
            //    The check used to live only inside fn_process_standard_expense,
            //    which is the OCR path — an expense typed in here with the wrong
            //    total posted in silence however far off it was.
            //    Deliberately after the expense is committed and not allowed to
            //    fail the save: a receipt that will not go into the books is
            //    worse than one that needs a second look.
            if (jobId) {
                const { data: flag, error: flagErr } = await supabase.rpc(
                    'fn_flag_expense_job_discrepancy',
                    { p_header_id: header.id },
                );
                if (flagErr) {
                    reportError(flagErr, 'manual-expense.fn_flag_expense_job_discrepancy');
                } else if ((flag as { flagged?: boolean } | null)?.flagged) {
                    toast('This does not match what the job records for parts — check your notifications.', {
                        icon: '⚠️',
                    });
                }
            }

            toast.success('Expense logged');
            onSuccess?.();
            onClose();
        } catch (e) {
            console.error('Manual expense save failed:', e);
            toast.error(errorMessage(e) || 'Failed to save expense');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-scrim/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-panel border border-line rounded-2xl shadow-2xl w-full max-w-xl flex flex-col max-h-[90vh]">
                <div className="flex justify-between items-center p-6 border-b border-line bg-raised">
                    <div>
                        <h2 className="text-xl font-bold text-fg">Add Manual Expense</h2>
                        <p className="text-sm text-fg-3 mt-1">Failsafe for when OCR isn't an option.</p>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={saving}
                        className="p-2 hover:bg-raised rounded-full transition-colors text-fg-3 hover:text-fg shrink-0 disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto custom-scrollbar space-y-4">
                    {/* Row: date + vendor */}
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Date">
                            <input
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                className="w-full bg-card border border-line-strong hover:border-line-glow focus:border-accent text-fg text-sm rounded-lg px-3 py-2.5 outline-none transition-colors"
                            />
                        </Field>
                        <Field label="Vendor">
                            <input
                                type="text"
                                value={vendor}
                                onChange={(e) => setVendor(e.target.value)}
                                placeholder="e.g. ACME Parts"
                                className="w-full bg-card border border-line-strong hover:border-line-glow focus:border-accent text-fg text-sm rounded-lg px-3 py-2.5 outline-none transition-colors placeholder-fg-4"
                            />
                        </Field>
                    </div>

                    <Field label="Description (optional)">
                        <input
                            type="text"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="What was purchased"
                            className="w-full bg-card border border-line-strong hover:border-line-glow focus:border-accent text-fg text-sm rounded-lg px-3 py-2.5 outline-none transition-colors placeholder-fg-4"
                        />
                    </Field>

                    <Field label="Category (HMRC SA103)">
                        <select
                            value={categoryName}
                            onChange={(e) => setCategoryName(e.target.value)}
                            className="w-full bg-card border border-line-strong hover:border-line-glow focus:border-accent text-fg text-sm rounded-lg px-3 py-2.5 outline-none transition-colors"
                        >
                            <option value="">— Pick a category —</option>
                            {categories.map((c) => (
                                <option key={c.id} value={c.name}>
                                    {c.name} {c.is_tax_deductible ? '' : '(non-deductible)'}
                                </option>
                            ))}
                        </select>
                    </Field>

                    {/* Row: gross + VAT */}
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Gross total (£)">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={grossAmount}
                                onChange={(e) => setGrossAmount(e.target.value)}
                                placeholder="0.00"
                                className="w-full bg-card border border-line-strong hover:border-line-glow focus:border-accent text-fg text-sm rounded-lg px-3 py-2.5 outline-none transition-colors placeholder-fg-4"
                            />
                        </Field>
                        <Field label={is_vat_registered ? `VAT (£)${vatAutoCalc ? ' · auto' : ''}` : 'VAT (not registered)'}>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={vatAmount}
                                onChange={(e) => { setVatAutoCalc(false); setVatAmount(e.target.value); }}
                                placeholder={is_vat_registered ? '0.00' : 'n/a'}
                                disabled={!is_vat_registered}
                                className="w-full bg-card border border-line-strong hover:border-line-glow focus:border-accent disabled:opacity-50 disabled:cursor-not-allowed text-fg text-sm rounded-lg px-3 py-2.5 outline-none transition-colors placeholder-fg-4"
                            />
                        </Field>
                    </div>

                    {/* Payment + job link */}
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Payment method">
                            <select
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value as typeof paymentMethod)}
                                className="w-full bg-card border border-line-strong hover:border-line-glow focus:border-accent text-fg text-sm rounded-lg px-3 py-2.5 outline-none transition-colors"
                            >
                                <option value="card">Card</option>
                                <option value="bank">Bank transfer</option>
                                <option value="cash">Cash</option>
                                <option value="other">Other</option>
                            </select>
                        </Field>
                        <Field label={<span className="flex items-center gap-1"><Link2 className="w-3 h-3" /> Link to job (optional)</span>}>
                            <select
                                value={jobId}
                                onChange={(e) => setJobId(e.target.value)}
                                className="w-full bg-card border border-line-strong hover:border-line-glow focus:border-accent text-fg text-sm rounded-lg px-3 py-2.5 outline-none transition-colors"
                            >
                                <option value="">— No job link —</option>
                                {activeJobs.map((job) => {
                                    const tag = job.vehicle_reg ? ` · ${job.vehicle_reg}` : '';
                                    const customer = job.customer_name || 'Unnamed';
                                    const done = job.status === 'completed' ? ' [Done]' : '';
                                    return (
                                        <option key={job.id} value={job.id}>
                                            {customer}{tag}{done}
                                        </option>
                                    );
                                })}
                            </select>
                        </Field>
                    </div>

                    {jobId && (
                        <p className="text-[11px] text-fg-4">
                            This purchase will reduce the Working Capital reservation on the linked job.
                        </p>
                    )}
                </div>

                <div className="p-6 border-t border-line bg-raised flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        disabled={saving}
                        className="px-4 py-2.5 bg-raised hover:bg-pressed disabled:opacity-50 text-fg rounded-lg text-sm font-medium transition-colors border border-line-strong"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="fd-btn-primary disabled:opacity-50 px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {saving ? 'Saving…' : 'Save expense'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
    return (
        <label className="block">
            <span className="text-xs font-medium text-fg-3 uppercase tracking-wider mb-1.5 block">
                {label}
            </span>
            {children}
        </label>
    );
}
